import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useMemo,
} from 'react';
import Script from 'next/script';
import { useTunnusContext } from 'contexts/TunnusContext';
import { apmRum } from 'services/apm-rum';
import {
  initSnowplowTracker,
  addConsentContext,
  setUser,
  clearUser,
  addUserProfile,
} from 'services/snowplowAnalytics/snowplowAnalytics';
import logger from 'services/logger';
import { useCookieConsent } from 'contexts/CookieConsentContext';
import { snowplowAnalyticsProperties } from 'services/properties/frontend';

declare global {
  interface Window {
    yleSnowplow?: (method: string, ...args: unknown[]) => void;
    yleSnowplowReady?: Promise<void>;
  }
}

type SnowplowContextType = {
  isSnowplowReady: boolean;
};

const SnowplowContext = createContext<SnowplowContextType>({
  isSnowplowReady: false,
});

type SnowplowProviderProps = {
  children: ReactNode;
};

export const SnowplowProvider: React.FC<SnowplowProviderProps> = ({
  children,
}) => {
  const consent = useCookieConsent();
  const {
    user: { transientId, isSubAccount, age } = {},
    isLoggedIn: isAuthenticated,
    yleTunnusInstance,
  } = useTunnusContext();

  const [isSnowplowScriptInPlace, setSnowplowScriptInPlace] = useState(false);
  const [isSnowplowInitialized, setSnowplowInitialized] = useState(false);
  const [isSnowplowUserReady, setSnowplowUserReady] = useState(false);
  const [isSnowplowConsentReady, setSnowplowConsentReady] = useState(false);

  const resolveRef = useRef<(() => void) | null>(null);
  const rejectRef = useRef<(() => void) | null>(null);
  const appliedIdentityRef = useRef<{
    isAuthenticated: boolean | undefined;
    transientId: string | undefined;
  }>({ isAuthenticated: undefined, transientId: undefined });

  const contextValue = useMemo(
    (): SnowplowContextType => ({
      isSnowplowReady:
        isSnowplowInitialized && isSnowplowUserReady && isSnowplowConsentReady,
    }),
    [isSnowplowInitialized, isSnowplowUserReady, isSnowplowConsentReady]
  );

  useEffect(() => {
    if (!window.yleSnowplowReady) {
      window.yleSnowplowReady = new Promise<void>((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
      });
    }
  }, []);

  // init snowplow
  useEffect(() => {
    if (!isSnowplowScriptInPlace) return;

    try {
      initSnowplowTracker();

      setSnowplowInitialized(true);
      resolveRef.current?.();
    } catch (error) {
      logger.error(error, 'Failed to initialize Snowplow tracker');
      if (error instanceof Error) apmRum?.captureError(error);
      rejectRef.current?.();
    }
  }, [isSnowplowScriptInPlace]);

  // update consent context
  useEffect(() => {
    if (isSnowplowInitialized && consent) {
      addConsentContext(consent);
      setSnowplowConsentReady(true);
    }
  }, [consent, isSnowplowInitialized]);

  // Applies the resolved user identity to Snowplow at most once per
  // (isAuthenticated, transientId) pair: clearUser()/addUserProfile() are not
  // idempotent (clearUser starts a new Snowplow session), and both the
  // context-derived effect below and the yleTunnusInstance message listener
  // can otherwise fire for the same login/logout.
  const applyIdentity = useCallback(
    (
      nextIsAuthenticated: boolean,
      nextTransientId: string | undefined,
      nextAge?: number,
      nextIsSubAccount?: boolean
    ) => {
      const applied = appliedIdentityRef.current;
      if (
        applied.isAuthenticated !== nextIsAuthenticated ||
        applied.transientId !== nextTransientId
      ) {
        appliedIdentityRef.current = {
          isAuthenticated: nextIsAuthenticated,
          transientId: nextTransientId,
        };

        if (nextIsAuthenticated && nextTransientId) {
          setUser(nextTransientId);
          if (nextAge && nextIsSubAccount) {
            addUserProfile(nextAge);
          }
        } else if (nextIsAuthenticated === false) {
          clearUser();
        }
      }
      setSnowplowUserReady(true);
    },
    []
  );

  // Resolves the identity already known when this provider mounts (e.g. a
  // session restored from a cookie), for which Tunnus never broadcasts a
  // message.
  useEffect(() => {
    if (!isSnowplowInitialized) return;

    if (isAuthenticated && transientId) {
      applyIdentity(isAuthenticated, transientId, age, isSubAccount);
    } else if (isAuthenticated === false) {
      applyIdentity(false, undefined);
    }
  }, [
    age,
    isAuthenticated,
    isSubAccount,
    transientId,
    isSnowplowInitialized,
    applyIdentity,
  ]);

  // Reacts to a login/logout happening after mount. yleTunnusInstance invokes
  // its listeners synchronously as soon as the SDK's own state changes, ahead
  // of TunnusContext's isLoggedIn/user (updated via an async BroadcastChannel
  // message) — so setUser() is guaranteed to land before anything that reacts
  // to isLoggedIn, such as useTrackSnowplowPageView's page view tracking.
  useEffect(() => {
    if (!yleTunnusInstance) return;
    if (!isSnowplowInitialized) return;

    const listener: Parameters<
      typeof yleTunnusInstance.addMessageListener
    >[0] = (message) => {
      if (message.type === 'tunnus-logged-in') {
        const { transientId, isSubAccount, age } = message.user;
        applyIdentity(true, transientId, age, isSubAccount);
      }
      if (message.type === 'tunnus-logged-out') {
        applyIdentity(false, undefined);
      }
    };

    yleTunnusInstance.addMessageListener(listener);
    return () => yleTunnusInstance.removeMessageListener(listener);
  }, [yleTunnusInstance, isSnowplowInitialized, applyIdentity]);

  return (
    <SnowplowContext.Provider value={contextValue}>
      <Script
        id="snowplow-load-script"
        onReady={() => setSnowplowScriptInPlace(true)}
      >
        {`;(function(p,l,o,w,i,n,g){if(!p[i]){p.GlobalSnowplowNamespace=p.GlobalSnowplowNamespace||[]; p.GlobalSnowplowNamespace.push(i);p[i]=function(){(p[i].q=p[i].q||[]).push(arguments) };p[i].q=p[i].q||[];n=l.createElement(o);g=l.getElementsByTagName(o)[0];n.async=1;n.crossOrigin=""; n.src=w;g.parentNode.insertBefore(n,g)}}(window,document,"script",${JSON.stringify(snowplowAnalyticsProperties.trackerUrl)},"yleSnowplow"));`}
      </Script>
      {children}
    </SnowplowContext.Provider>
  );
};

export function useSnowplow(): SnowplowContextType {
  return useContext(SnowplowContext);
}
